<template>
  <div class="p-1">
    <div class="flex justify-between flex-wrap items-center mb-2">
      <h3 class="text-2xl">Pagos</h3>
      <a-button :to="{ name: 'create_payment' }" class="w-full md:w-auto" v-if="user.isAdmin">
        Registrar pago
      </a-button>
    </div>

    <a-table
        class="overflow-scroll"
      :columns="columns"
      :source="pagination.data"
      :loading="loading"
      toolbar-column-filter
      :pagination="pagination"
      @paginate="search({ page: $event })"
      @delayed-filter:student="search({ student_like: $event })"
      @toolbar(refresh)="search({ page: 1 })">

      <template #toolbar(search)>
        <a-input
            is-search
            placeholder="Buscar..."
            @search="search({ search: $event, page: 1 })" />
      </template>

      <template #toolbar>
        <v-select
          class="w-56"
          @input="search({ status: $event, page: 1 })"
          :clearable="false"
          v-model="query.status"
          :options="[
              { label: 'Pendiente por revisar', value: '1' },
              { label: 'Aprobado', value: '2' },
              { label: 'Rechazado', value: '3' },
              { label: 'Todos', value: null }
          ]"
          :reduce="$0 => $0.value"/>
      </template>

      <template #td(actions)="{ item }">
        <div class="flex space-x-2">
          <a-button
            v-if="item.status === 1"
            v-tooltip="`Ver pago`"
            @click="handleOpenInstallmentsModal(item)">
            <a-icon name="eye" class="text-primary" />
          </a-button>
<!--          <a-dropdown-item :to="{ name: 'create_period', query: { update: item.id } }">-->
<!--            Actualizar-->
<!--          </a-dropdown-item>-->
          <a-button @click="$router.push({ name: 'create_payment', query: { update: item.id } })" v-tooltip="`Editar pago`" v-if="item.status === 1">
            <a-icon name="edit" class="text-yellow-600" />
          </a-button>
          <a-button @click="deletePayment(item.id)" v-tooltip="`Eliminar pago`" v-if="item.status === 1">
            <a-icon name="trash" class="text-red-600" />
          </a-button>
          <template v-else>
            -
          </template>
        </div>
      </template>
    </a-table>
    <a-modal 
      title="Resumen de pago"
      :show="modals.installments"
      hideTopCancel
    >
      <div class="space-y-4" style="height: 520px;">
        <template v-if="loadingInstallments">
          <div class="flex items-center justify-center h-full">
            <a-loader class="h-12 w-12 mt-4 text-primary"/>
          </div>
        </template>
        <template v-else>
          <div class="flex flex-col gap-4">
            <div class="flex flex-col gap-2 p-4 flex-grow-0">
              <div class="flex items-center gap-2">
                <span class="font-bold">Fecha:</span>
                <span>{{ moment(selectedPayment.date).locale('es').format('D [de] MMMM [del] YYYY') }}</span>
              </div>
              <div class="flex items-center gap-2">
                <span class="font-bold">Tipo de pago:</span>
                <span>{{ selectedPayment.type_name }}</span>
              </div>
              <div class="flex items-center gap-2">
                <span class="font-bold">Número de recibo:</span>
                <span 
                  :class="['truncate custom-width', selectedPayment.receipt_number ? 'cursor-pointer text-primary' : '']"
                  :title="selectedPayment.receipt_number"
                  @click="copyToClipboard(selectedPayment.receipt_number)"
                >
                  {{ selectedPayment.receipt_number || 'No aplica' }}
                </span>
              </div>
              <div class="flex items-center gap-2">
                <span class="font-bold">Monto:</span>
                <span>{{ selectedPayment.amount / 100 }}$</span>
              </div>
            </div>
            <div class="flex flex-col gap-4 overflow-y-auto h-72 flex-grow">
              <template v-if="periodFee && periodFee.fee_installments.length > 0">
                <template v-if="validateFirstPayment()">
                  <div class="flex items-center justify-center h-full">
                    <span class="text-2xl text-center text-gray-500">Este pago corresponde al monto inicial de inscripción</span>
                  </div>
                </template>
                <template v-else>
                  <template v-if="validateFullPayment()">
                    <span class="text-center text-gray-500">Este pago cubre el monto inicial de inscripción y el monto total de las cuotas</span>
                  </template>
                  <div
                    v-for="(installment, index) in periodFee.fee_installments"
                    :key="index"
                    :class="[
                      'rounded-lg p-5 border-solid content-center h-24 bg-white border-l-4',
                      installment.paid_amount_e2 < installment.amount_e2 
                        ? isEnabled(installment.id)
                          ? 'border-red-400 shadow-lg'
                          : 'bg-gray-100 border-gray-30'
                        : 'border-primary shadow-lg bg-gray-100'
                    ]"
                  >
                    <div class="flex justify-between items-center">
                      <div class="flex flex-col gap-1">
                        <div class="flex items-center gap-2">
                          <a-icon name="calendar" />
                          <span class="text-gray-700 font-medium">
                            {{ moment(installment.date).locale('es').format('D [de] MMMM') }}
                          </span>
                        </div>
                        <template v-if="installment.is_late">
                          <span class="text-red-500 text-xs">
                            La cuota está atrasada, por lo que se le cobrará un extra de {{ installment.late_extra_amount_string }}
                          </span>
                        </template>
                        <template v-else-if="getDaysUntilLateFee(installment).isAfter">
                          <span class="text-yellow-400 text-xs">
                            Al estudiante le quedan {{ getDaysUntilLateFee(installment).days }} días para pagar o tendrá que abonar {{ installment.late_extra_amount_string }} extra
                          </span>
                        </template>
                      </div>
                      <div class="flex flex-col items-end gap-2 relative">
                        <div class="flex items-center gap-2">
                          <template v-if="selectedInstallments[index] && selectedInstallments[index].paid_amount_e2 >= selectedInstallments[index].amount_e2">
                            <div class="rounded-full border-2 border-primary w-6 h-6 flex items-center justify-center bg-primary">
                              <a-icon name="check" class="text-white" stroke-width="2" />
                            </div>
                          </template>
                          <template v-else-if="selectedInstallments[index] && selectedInstallments[index].paid_amount_e2 > 0">
                            <div class="rounded-full border-2 border-yellow-400 bg-yellow-400 w-6 h-6 flex items-center justify-center">
                              <a-icon name="minus" class="text-white" stroke-width="2" />
                            </div>
                          </template>
                          <span class="text-gray-700 font-medium">
                            {{ getInstallmentAmount(index) }}
                          </span>
                        </div>
                        <div 
                          v-if="selectedInstallments[index]" 
                          class="text-xs w-full flex flex-col gap-1" 
                        >
                          <template v-if="selectedInstallments[index].is_new">
                            <div class="flex items-center justify-between w-40">
                              <span class="font-bold">Monto abonado:</span> 
                              <span>{{ (selectedInstallments[index].paid_amount_e2 / 100) }}$</span>
                            </div>
                            <template v-if="selectedInstallments[index].paid_amount_e2 < selectedInstallments[index].amount_e2">
                              <div class="flex items-center justify-between w-40">
                                <span class="font-bold text-red-500">Restante:</span> 
                                <span>{{ (selectedInstallments[index].amount_e2 - selectedInstallments[index].paid_amount_e2) / 100 }}$</span>
                              </div>
                            </template>
                          </template>
                          <template v-else-if="selectedInstallments[index].paid_amount_e2 >= selectedInstallments[index].amount_e2">
                            <a-badge class="bg-primary text-white">
                              Aprobado
                            </a-badge>
                          </template>
                        </div>
                      </div>
                    </div>
                  </div>
                </template>
              </template>
              <template v-else>
                <div class="flex items-center justify-center h-full">
                  <span class="text-2xl text-center text-gray-500">No hay cuotas registradas</span>
                </div>
              </template>
            </div>
            <div class="flex justify-between items-center flex-grow-0">
              <div>
                <a-button
                  @click="modals.installments = false"
                  class="w-full"
                >
                  Cancelar
                </a-button>
              </div>
              <div class="flex gap-2">
                <a-button
                  @click="handleChangePaymentStatus({ id: selectedPayment.id, status: 3, text: 'rechazar' })"
                  class="w-full"
                  mode="dangerous"
                  :disabled="sendingPayment"
                >
                  Rechazar
                </a-button>
                <a-button
                  @click="handleChangePaymentStatus({ id: selectedPayment.id, status: 2, text: 'aprobar' })"
                  class="w-full"
                  mode="primary"
                  :disabled="sendingPayment"
                >
                  Aprobar
                </a-button>
              </div>
            </div>
          </div>
        </template>
      </div>
    </a-modal>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import moment from 'moment'
import Vue from 'vue'
export default {
  data: () => ({
    query: {
      limit: 10,
      with: 'student,period',
      search: '',
      status: '1',
      sort_by: 'date,desc'
    },
    timeout: null,
    modals: {
      installments: false,
    },
    selectedPayment: [],
    loadingInstallments: true,
    periodFee: null,
    selectedInstallments: [],
    sendingPayment: false
  }),
  computed: {
    ...mapState({
      pagination: state => state.payments.pagination,
      loading: state => state.payments.loading,
      user: ({ session }) => session.user
    }),
    columns() {
      return [
        { title: 'Fecha', key: 'date', display: true },
        { title: 'Factura', key: 'receipt_number', mutate: ({ key }) => key || "No aplica", display: true },
        { title: 'Monto', key: 'amount', mutate: ({ key }) => `${'$' + parseInt(key / 100).toFixed(0)}`, display: true },
        { title: 'Tipo', key: 'type', mutate: ({ key }) => this.parseType(key), display: true },
        { title: 'Estado', key: 'status', mutate: ({ key }) => this.parseStatus(key), display: true },
        { title: 'Estudiante', key: 'student', mutate: ({ key }) => `${key.first_name} ${key.last_name}`, display: true, filter: true, preventLocalFilter: true },
        { title: 'Período', key: 'period', mutate: ({ key }) => `${key.year}-${key.number}`, display: true },
        { title: 'Acciones', slot: 'actions', style: () => ({ width: `180px` }), alignment: 'right', columnFilterable: false, display: this.user.isAdmin },
      ].filter(column => column.display)
    }
  },
  methods: {
    moment,
    copyToClipboard(text) {
      if (!text) return;
      navigator.clipboard.writeText(text);
      this.$message.success('Recibo copiado al portapapeles');
    },
    handleChangePaymentStatus({ id, status, text }) {
      Vue.prototype.$confirm({
        message: `¿Está seguro que quiere ${text} este pago? Esta acción no se puede deshacer`,
        button: {
            no: 'No',
            yes: 'Si'
        },
        callback: confirm => {
          if (confirm) {
            this.sendingPayment = true;
            this.changePaymentStatus({ id, status })
              .then(() => {
                this.modals.installments = false;
                this.search({ ...this.query, page: this.$route.query.page || 1 })
              })
              .finally(() => this.sendingPayment = false);
          }
        }
      })
    },
    parseType(type) {
      return {
        1: 'Efectivo',
        2: '100% Banco',
        3: 'Transferencia Bancaria',
        4: 'Transferencia de Curso',
        5: 'Zelle'
      }[type]
    },
    parseStatus(status) {
      return {
        1: 'Pendiente por revisar',
        2: 'Aprobado',
        3: 'Rechazado'
      }[status]
    },
    isEnabled(id) {
      const find = this.selectedInstallments.find(installment => installment.id === id);
      return find ? true : false;
    },
    getDaysUntilLateFee(installment) {
      if (installment) {
        const today = moment().startOf('day');
        const installmentDate = moment(installment.date).startOf('day');
        const diffInDays = installmentDate.diff(today, 'days');
        const dueDateWithGrace = installmentDate.clone().add(this.periodFee.installment_grace_period_days, 'days');
        const daysLeft = dueDateWithGrace.diff(today, 'days');
        const isSameDay = installmentDate.isSame(today, 'day');
        return {
          isAfter: isSameDay ? false : diffInDays <= 0,
          days: daysLeft
        };
      } return {
        isAfter: false,
        days: 0
      };
    },
    getInstallmentAmount(index){
      if(this.selectedInstallments[index]){
        return `${this.selectedInstallments[index].amount_e2 / 100}$`;
      }
      return this.periodFee.fee_installments[index].amount_string;
    },
    handleOpenInstallmentsModal(payment){
      this.selectedPayment = payment;
      this.loadingInstallments = true;
      this.modals.installments = true;
      this.$repository.students
        .getStudentPeriodFee(payment.student_id)
        .then(({ data }) => {
          this.periodFee = data;
          const filteredInstallments = data.fee_installments.filter(installment => installment.paid_amount_e2 >= installment.amount_e2);
          this.selectedInstallments = filteredInstallments;
      
          let totalAmount = payment.amount;
          data.fee_installments
            .filter(instalment => instalment.paid_amount_e2 < instalment.amount_e2)
            .forEach(installment => {
              const amountPaid = installment.paid_amount_e2;
              const amountToPay = installment.is_late
              ? installment.amount_e2 + installment.late_extra_amount_e2 - amountPaid 
              : installment.amount_e2 - amountPaid;
              if (amountToPay > 0 && totalAmount > 0) {
                const totalToPay = Math.min(totalAmount, amountToPay);
                totalAmount = Math.max(0, totalAmount - totalToPay);
                this.selectedInstallments.push({
                  ...installment,
                  amount_e2: amountToPay,
                  is_new: true,
                  paid_amount_e2: totalToPay
                });
              }
            });
        })
        .catch(() => {
          this.periodFee = null;
        })
        .finally(() => {
          this.loadingInstallments = false;
        })
    },
    handleStatusChange(status) {
      if (status) {
        const query = this.$repositoryUtils.withoutInvalidValues({...this.$route.query, status })
        this.$router.replace({ query })
          .catch(() => null)
      } else {
        let routeQuery = this.$route.query
        delete routeQuery.status
        delete this.query.status

        const query = this.$repositoryUtils.withoutInvalidValues(query)

        this.$router.replace({ query })
          .catch(() => null)
      }

      this.search()
    },
    ...mapActions({
      fetchPayments: 'payments/fetchAll',
      deletePayment: 'payments/delete',
      changePaymentStatus: 'payments/changePaymentStatus'
    }),
    getTypeString(type) {
      switch (type) {
        case 1: return "Efectivo"
        case 2: return "100% Banco"
        case 3: return "Transferencia Bancaria"
        case 4: return "Transferencia de Curso"
        case 5: return "Zelle"
        default: "Tipo no definido"
      }
    },
    getStatusString(state) {
      switch (state) {
        case 1: return "Pendiente por revisar"
        case 2: return "Aprobado"
        case 3: return "Rechazado"
        default: return "Estado no definido"
      }
    },
    search(query = {}) {
      this.fetchPayments(this.$repositoryUtils.withoutInvalidValues({ ...this.query, ...query }))
    },
    validateFirstPayment(){
      if(this.periodFee?.payments){
        const filteredPayments = this.periodFee.payments.filter(payment => payment.status === 2);
        if(filteredPayments.length === 0){
          return (parseInt((this.selectedPayment.amount / 100).toFixed(0)) === this.periodFee.initial_price);
        } return false;
      } return false;
    },
    validateFullPayment(){
      if(this.periodFee?.payments){
        const filteredPayments = this.periodFee.payments.filter(payment => payment.status === 2);
        if(filteredPayments.length === 0){
          const totalToPay = this.periodFee.fee_installments
            .reduce((acc, installment) => {
              acc += installment.amount_e2;
              return acc;
            }, 0);
          const amountPaid = parseInt((this.selectedPayment.amount / 100).toFixed(0));
          return parseInt((totalToPay / 100).toFixed(0)) === amountPaid - this.periodFee.initial_price;
        } return false;
      } return false;
    } 
  },
  mounted() {
    this.search({ ...this.query, page: this.$route.query.page || 1 })
  },
  created() {
    this.query = {
      ...this.query,
      ...this.$route.query
    }
  }
}
</script>
<style>
.custom-width {
  max-width: 100px;
  @media(min-width: 768px) {
    max-width: 200px;
  }
}
</style>
